import { useMemo, useState } from "react";
import { arrayOf, bool, func, number, string, object, oneOf } from "prop-types";
import { EstimationUnits } from "@repo/utils";
import { useTranslation } from "react-i18next";
import {
  removeWorkTypeQuery,
  editCustomWorkTypeQuery,
  updateRateCardCategoryQuery,
} from "@query";
import { useWorkspaceWebsocket } from "@hooks";
import { isNonExpandActionClick, MAX_VALUES } from "@utils";
import {
  ValueCell,
  PinnableCell,
  RowGroup,
  Row,
  ExpandContainer,
} from "@tools";
import { Alert, SortableList } from "@components";
import { CircularProgress, Collapse, IconButton } from "@material-ui/core";
import { RateRole } from "./RateRole";
import { RateElementColorEditor } from "./RateElementColorEditor";
import { NewRateElement } from "./NewRateElement";
import { NewRateElementRow } from "./NewRateElementRow";
import { Trash } from "@assets";
import { VisibilityOffOutlined, VisibilityOutlined } from "@material-ui/icons";
import { RateNameInput } from "@views/Utility/Settings/subviews/Project/subviews/Rates/components/RateNameInput";

export const RateCategory = ({
  workType,
  currency,
  companyCurrency,
  dayTime,
  americanPriceFormat,
  estimationUnit,
  onTeamMemberAdd,
  onRoleReorder,
  onChange,
  onRemove,
  usedElementNames,
}) => {
  const { t } = useTranslation();
  const socket = useWorkspaceWebsocket();
  
  const [ addingNewWorkType, setAddingNewWorkType ] = useState(false);
  const [ expanded, setExpanded ] = useState(false);
  const [ isLoading, setLoading ] = useState(false);
  
  const [ showRemovePrompt, setShowRemovePrompt ] = useState(false);
  const [ showRemoveError, setShowRemoveError ] = useState(false);
  
  const [ name, setName ] = useState(workType.name || "");
  const [ costRate, setCostRate ] = useState(workType.costRate);
  const [ salesRate, setSalesRate ] = useState(workType.salesRate);
  
  
  const level = 1;
  const backgroundColor = workType.backgroundColor || "";
  const hidden = workType.hidden;
  const canRemove = Boolean(workType.removable);
  
  const usedRoleNames = useMemo(() => (
    workType.roles.map(r => r.roleName).filter(r => r)
  ), [ workType.roles ]);
  
  const handleCostRateChange = async (value) => {
    try {
      const v = value / (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime);
      setCostRate(v);
      await updateRateCardCategoryQuery(workType.categoryId, { costRate: value });
      onChange("costRate", null, workType.categoryId, v);
    } catch (err) {
      setCostRate(workType.costRate);
    }
  };
  
  const handleSalesRateChange = async (value) => {
    try {
      const v = value / (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime);
      setSalesRate(v);
      await updateRateCardCategoryQuery(workType.categoryId, { salesRate: value });
      onChange("salesRate", null, workType.categoryId, v);
    } catch (err) {
      setSalesRate(workType.costRate);
    }
  };

  const handleColorChange = async (value) => {
    await editCustomWorkTypeQuery(workType.id, workType.name, value);
    onChange("backgroundColor", null, workType.categoryId, value);
  }

  const handleNameChange = async (newName) => {
    if(!newName || newName === name)
    return;
    
    setName(newName);
    await editCustomWorkTypeQuery(workType.id, newName);
    onChange("name", null, workType.categoryId, newName);
  }

  const handleRemove = async () => {
    try {
      setLoading(true);
      const status = await removeWorkTypeQuery(workType.id);
      if (!status)
        throw "no status";
      socket?.requestWorkTypeRemove(workType.id);
      onRemove(null, workType.categoryId);
      setShowRemovePrompt(false);
    } catch (e) {
      setShowRemoveError(true);
    } finally {
      setLoading(false);
    }
  }
  
  const handleHide = async () => {
    setLoading(true);
    await updateRateCardCategoryQuery(workType.categoryId, { isActive: hidden });
    onChange("hidden", null, workType.categoryId, !hidden);
    setLoading(false);
  }

  const handleExpand = (event) => {
    if (isNonExpandActionClick(event))
      return;
    setExpanded(!expanded);
    // onChange("expanded", null, workType.categoryId, !expanded);
  }
  
  const handleRoleReorder = (list, movedElementId, _, oldIndex, newIndex) => {
    onRoleReorder(workType.categoryId, list, movedElementId, oldIndex, newIndex);
  }
  
  const handleErrorClose = () => {
    setShowRemoveError(false);
    setShowRemovePrompt(false);
  }

  return (
    <RowGroup tableDepth={level} isActive={expanded}>
      <Row
        useExpandCell
        useDragHandleCell
        expandOnClick
        expanded={expanded}
        onExpand={handleExpand}
        visible={!hidden}
        isFirstGroupRow
        isLastGroupRow={!expanded}
        tableDepth={level}
      >
        <PinnableCell
          keepSmallestSize
          pinnedLeft
          tableDepth={level}
          allowOverflowDisplay
          containers={<ExpandContainer
            tableDepth={level}
            expandable
            expanded={expanded}
          />}
        >
          <RateNameInput
            value={name}
            onAccept={handleNameChange}
            usedElementNames={usedElementNames}
            placeholder={t("common.unnamed")}
            showWorkTypeAlert={!canRemove}
          />
        </PinnableCell>
  
        <ValueCell size="small">
          <RateElementColorEditor
            elementName={name}
            color={backgroundColor}
            onColorChange={handleColorChange}
          />
        </ValueCell>
  
        <ValueCell
          size="large"
          displayContent
          editable extendPadding
          value={costRate * (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime)}
          onValueChange={handleCostRateChange}
          max={estimationUnit === EstimationUnits.HOURS ? MAX_VALUES.RATE_HOUR : MAX_VALUES.RATE_DAY}
          symbolStart={companyCurrency?.symbolStart || ""}
          symbolEnd={companyCurrency?.symbolEnd || ""}
          americanFormat={americanPriceFormat}
          showDecimals
          applySuffixSpacing
        />
  
        <ValueCell
          size="large"
          displayContent
          editable extendPadding
          value={salesRate * (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime)}
          onValueChange={handleSalesRateChange}
          max={estimationUnit === EstimationUnits.HOURS ? MAX_VALUES.RATE_HOUR : MAX_VALUES.RATE_DAY}
          symbolStart={currency?.symbolStart || ""}
          symbolEnd={currency?.symbolEnd || ""}
          americanFormat={americanPriceFormat}
          showDecimals
          applySuffixSpacing
        />
  
        <ValueCell size="small">
          <IconButton onClick={canRemove ? () => setShowRemovePrompt(true) : handleHide} size="small">
            {
              isLoading && <CircularProgress size={16} />
            }
            {
              !isLoading && (
                canRemove
                  ? <Trash color="error" />
                  : hidden
                    ? <VisibilityOffOutlined color="primary" />
                    : <VisibilityOutlined color="primary" />
              )
            }
          </IconButton>
        </ValueCell>
      </Row>
      
      <Alert
        open={showRemovePrompt}
        title={t("views.settings.projects.rate_card.remove_prompt.category.title")}
        onCancel={() => setShowRemovePrompt(false)}
        onAccept={handleRemove}
        acceptText={isLoading ? <CircularProgress color="inherit" size={16} /> : t("common.delete")}
      >
        {t("views.settings.projects.rate_card.remove_prompt.category.content")}
      </Alert>
      
      <Alert
        open={showRemoveError}
        title={t("views.settings.projects.rate_card.remove_error.title")}
        onAccept={handleErrorClose}
        acceptText={t("common.close")}
      >
        {t("views.settings.projects.rate_card.remove_error.content", { workTypeName: name })}
      </Alert>
      
      <Collapse
        in={expanded}
        className="w-full"
        mountOnEnter
        unmountOnExit
      >
        <SortableList
          list={workType.roles}
          path="role"
          group="role"
          onListReorder={handleRoleReorder}
          displayAsColumnFlexbox
        >
          {
            workType.roles?.map((role) => (
              <RateRole
                key={`role-${workType.categoryId}-${role.id}`}
                role={role}
                parentId={workType.categoryId}
                currency={currency}
                companyCurrency={companyCurrency}
                dayTime={dayTime}
                americanPriceFormat={americanPriceFormat}
                estimationUnit={estimationUnit}
                onChange={onChange}
                onRemove={onRemove}
                usedElementNames={usedRoleNames}
              />
            ))
          }
        </SortableList>
        {
          addingNewWorkType
            ? <NewRateElement
              level={2}
              parentId={workType.categoryId}
              usedElementNames={usedRoleNames}
              onCreate={onTeamMemberAdd}
              onCancel={() => setAddingNewWorkType(false)}
            />
            : <NewRateElementRow
              onClick={() => setAddingNewWorkType(true)}
            />
        }
      </Collapse>
    </RowGroup>
  );
};

RateCategory.propTypes = {
  workType: object.isRequired,
  currency: object,
  companyCurrency: object,
  dayTime: number,
  americanPriceFormat: bool,
  estimationUnit: oneOf([EstimationUnits.HOURS, EstimationUnits.DAYS]).isRequired,
  onChange: func.isRequired,
  onRemove: func.isRequired,
  onTeamMemberAdd: func.isRequired,
  onRoleReorder: func.isRequired,
  usedElementNames: arrayOf(string).isRequired,
};
